import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as jose from "jose";
import { importKey } from "./importKey";
import { Input, Typography } from "antd";
import {
  cardCvcData,
  cardExpiryDateData,
  cardHolderNameData,
  cardNumberData,
} from "../slices/CardSlice";
import InnerHTML from "dangerously-set-html-content";
import { getPopDimensionDetails, isEmpty, isFireFox, isValidUrl } from "./utils";
import { selectPopWindowIframeUpiIntent } from "../slices/PaymentButtonSlice";
import { useTranslation } from "react-i18next";
import { resetPaymentInitiation, resetPaymentInitiationLoading } from "../slices/PaymentInitiationSlice";
import ErrorModal from "../ant-design/components/modals/errorModal/ErrorModal";
import { InfoCircleOutlined } from "@ant-design/icons";
import PaymentContext from "../context/payment.context";
import { startAutoRefund } from "../slices/AutoRefundSlice";

const WAIT_TIME_FOR_UPI_INTENT_PAYMENT = 12000;
const PaymentResponseHandler = ({ isIframe, popWindowIframeRef, stopStatusCall = () => { } }) => {
  const { t } = useTranslation();
  const { popUpWindowIframeIos, showErroModalForUpiIntent, setShowErroModalForUpiIntent, setShowCancelBtn, setCancelBtnFunc } = useContext(PaymentContext);
  const dispatch = useDispatch();
  const [response, setResponse] = useState(undefined);
  const paymentInitiationData = useSelector((state) => state.paymentInitiation.paymentInitiationData);
  const cardNumber = useSelector((state) => cardNumberData(state));
  const cardHolderName = useSelector((state) => cardHolderNameData(state));
  const cardExpiryDate = useSelector((state) => cardExpiryDateData(state));
  const cardCvc = useSelector((state) => cardCvcData(state));
  const formDivRef = useRef();
  const formRef = useRef();
  const [processedResponse, setProcessedResponse] = useState();
  const { isIphone, isAndroid, isChromeIos } = useSelector((state) => state.mobileLayout);
  const hasUserLeftTheCheckoutScreenRef = useRef(false);
  const isIosUserHasBeenNotifiedRef = useRef(false);
  const isAndroidUserNotifiedForOtherUPIs = useRef(false);
  const erroModalDescriptionText = useRef("PAYMENT_COULDNOT_PROCESSED");
  const popUpWindowForIntentForAndroidRef = useRef();
  const popUpWindowAndroidIntervalRef = useRef();
  const iosSafariIntervalRef = useRef();
  // The UPI intent listeners and timers below are never removed. After the shopper cancels and starts a new
  // payment, the ones of the old payment keep running, so they must do nothing once this handler is gone.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const params = new URLSearchParams(window.location.search);
  const showGpay = params.get("gp");
  const showPhonepe = params.get("pp");
  const showPaytm = params.get("pm");
  const hmh = params.get("hmh");
  const isNativeApp = (showPaytm || showPhonepe || showGpay || hmh) ? true : false;

  useEffect(() => {
    if (paymentInitiationData === undefined && response === undefined) {
      return;
    }
    if (paymentInitiationData !== response) {
      setResponse(paymentInitiationData);
    }
  }, [paymentInitiationData]);

  useEffect(() => {
    (async () => {
      const encryptionWork = {};
      const encryptAction = response?.actions?.find(
        (action) => action.type === "encrypt"
      );
      if (encryptAction) {
        const publicKey = await importKey(encryptAction.encryptionKey);

        const [expiry_month, expiry_year] = cardExpiryDate.split("/");
        const cardNo = cardNumber?.replace(/\s/g, "");
        const cardDetails = {
          number: cardNo,
          expiry_month: expiry_month,
          expiry_year: `20${expiry_year}`,
          holder_name: cardHolderName,
          cvv: cardCvc,
        };

        const jwe = await new jose.CompactEncrypt(
          new TextEncoder().encode(JSON.stringify(cardDetails))
        )
          .setProtectedHeader({
            alg: encryptAction.algo,
            enc: "A128GCM",
            kid: encryptAction.keyId,
            "x5t#S256": encryptAction.certificateFingerprint,
          })
          .encrypt(publicKey);

        encryptionWork[encryptAction.outputField] = jwe;
        setProcessedResponse((prev) => {
          const newResponse = { ...response };
          newResponse.actions = response?.actions?.map((action) => {
            const newAction = { ...action };
            newAction.data = { ...action.data, ...encryptionWork };
            return newAction;
          });
          return newResponse;
        });
      } else {
        setProcessedResponse(response);
      }
    })();
  }, [response]);
  const isAutoSubmitForm = () => {
    if (isEmpty(processedResponse)) return
    let isHtml = processedResponse?.actions?.some(d => d.type === 'html');
    return isHtml;
  };

  const handleCancelButtonVisibility = () => {
    // a listener of an old payment must not replace the Cancel button of the current one
    if (!isMountedRef.current) return;
    setShowCancelBtn(true);
    // The shopper taps Cancel. With auto refund on and the UPI app already opened (the page was hidden),
    // the payment flow is over, so the auto refund loader starts instead of failing right away.
    // Cancelling before the app opened is a plain cancel: the timer only starts after the redirect.
    const setFunc = async () => {
      const hasRedirectedToApp = hasUserLeftTheCheckoutScreenRef.current;
      if (!hasRedirectedToApp || !(await dispatch(startAutoRefund()))) {
        setShowErroModalForUpiIntent(true);
      }
    };
    setCancelBtnFunc(() => setFunc);
  }
  const getFormDataForRedirection = () => {
    let formArr = [];
    let actionData = processedResponse?.actions;

    if (isEmpty(actionData)) return formArr[0];
    actionData.forEach((action) => {
      if (action?.type === "redirect") {
        if (isValidUrl(action?.url)) {
          const formElement = document.createElement('form');
          formElement.method = action?.method;
          // formElement.action = getPaymentCompletionUrl(action?.url);
          formElement.action = action?.url;

          // add input
          Object.entries(addQueryParamsToObject(action?.url, action?.data)).forEach((entry, i) => {
            const input = document.createElement('input');
            input.key = i
            input.type = 'hidden';
            input.name = entry[0];
            input.value = entry[1];
            formElement.appendChild(input);
          })
          formArr.push(formElement)
        }
      } else if (action?.type === "html" && action?.htmlPageString !== undefined) {

        if (isIframe) {
          return formArr.push(action.htmlPageString)
        }
        if (document.getElementById("autosubmit-form") !== null) {
          formArr.push(document.getElementById("autosubmit-form"));
        }
        if (document.getElementById("payment_post") !== null) {
          formArr.push(document.getElementById("payment_post"));
        }
        if (document.getElementById("threedsChallengeRedirectForm") !== null) {
          formArr.push(document.getElementById("threedsChallengeRedirectForm"));
        }
        if (formArr.length === 0) {
          const container = document.getElementById("dynamic-html-container");
          const form = container?.querySelector("form");
          if (form) formArr.push(form);
        }
      } else if (action?.type === "appRedirect" && !isEmpty(formRef.current)) {

        if (isIphone && !isEmpty(popUpWindowIframeIos) && isIframe) {
          hasUserLeftTheCheckoutScreenRef.current = false;
          const formClone = formRef.current.cloneNode(true); // Clone the form with its child elements
          popUpWindowIframeIos.document.body.appendChild(formClone); // Append the cloned form
          formClone.submit();
          handleCancelButtonVisibility();
          handleUpiNotOpenedFallbackForIos(popUpWindowIframeIos);
          // formRef.current.submit();
        } else {
          if (isIphone) {
            handleCancelButtonVisibility();
            formRef.current.submit();
          } else {
            formRef.current.submit();
            handleCancelButtonVisibility();
            handleUpiNotOpenedFallbackForAndroid();
          }
        }
      }
    });

    return formArr[0];
  };
  const handleIframeEvents = () => {
    const { width, height, left, top } = getPopDimensionDetails();
    let redirectionForm = getFormDataForRedirection();
    if (!redirectionForm) return

    if (popWindowIframeRef?.current && !popWindowIframeRef?.current?.closed) {
      if (redirectionForm && !isAutoSubmitForm()) {
        popWindowIframeRef.current.document.body.appendChild(redirectionForm);
        redirectionForm.submit();
      } else {
        const iframeDocument = popWindowIframeRef.current.document;
        iframeDocument.open();
        iframeDocument.write(redirectionForm);
        iframeDocument?.close();
        const form = iframeDocument.querySelector('form');
        if (form) {
          form.submit();
        }
      }
    }
  };

  // UPI INTENT
  const handlePrimaryClick = () => {
    // reset all
    clearInterval(iosSafariIntervalRef.current);
    hasUserLeftTheCheckoutScreenRef.current = false;
    isIosUserHasBeenNotifiedRef.current = false;
    isAndroidUserNotifiedForOtherUPIs.current = false;
    erroModalDescriptionText.current = "PAYMENT_COULDNOT_PROCESSED";
    stopStatusCall();
    setShowErroModalForUpiIntent(false);
    dispatch(resetPaymentInitiationLoading());
    dispatch(resetPaymentInitiation());
    setShowCancelBtn(false);
  }
  const handleUpiNotOpenedFallbackForAndroid = () => {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // APP OPENED
        hasUserLeftTheCheckoutScreenRef.current = true;
      }
      if (document.visibilityState === "visible") {
        handleCancelButtonVisibility();
      }
    });
    window.addEventListener('blur', () => {
      isAndroidUserNotifiedForOtherUPIs.current = true;
    })
    if (!isNativeApp) {
      setTimeout(() => {
        if (isMountedRef.current && !hasUserLeftTheCheckoutScreenRef.current && !isAndroidUserNotifiedForOtherUPIs.current) {
          // APP NOT OPENED
          erroModalDescriptionText.current = "NO_UPI_APPS_FOUND";
          stopStatusCall();
          setShowErroModalForUpiIntent(true);
          hasUserLeftTheCheckoutScreenRef.current = false;
        }
      }, 2000);
    }
    window.addEventListener('focus', function () {
      // wait if the payment is done or not after opening the UPI app
      handleCancelButtonVisibility();
    });
  }
  const handleUpiNotOpenedFallbackForIos = (doc = window) => {
    // CHROME IOS
    if (isChromeIos) {
      // The check below stops at the first visible tick, so the page leaving is tracked here.
      // Cancel only starts auto refund after the UPI app was opened.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          hasUserLeftTheCheckoutScreenRef.current = true;
        }
      });
      iosSafariIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'hidden') {
          // page is not visible to the user
        } else {
          // page is visible to the user
          stopStatusCall();
          clearInterval(iosSafariIntervalRef.current);
          handleCancelButtonVisibility();
          // setTimeout(() => {
          //   setShowErroModalForUpiIntent(true);
          // }, WAIT_TIME_FOR_UPI_INTENT_PAYMENT);
        }
      }, 500); // Check every 500ms
      return
    } else {
      // SRAFARI
      doc.addEventListener('blur', function () {
        // USER HAS BEEN NOTIFIED ABOUT UPI INTENT APP
        isIosUserHasBeenNotifiedRef.current = true;
      });
      doc.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // USER LEFT CHECKOUT PAGE
          hasUserLeftTheCheckoutScreenRef.current = true;
        }
      });
      window.addEventListener('focus', () => {
        if (isIosUserHasBeenNotifiedRef.current && hasUserLeftTheCheckoutScreenRef.current) {
          // APP OPENED AND USER IS BACK TO CHECKOUT
          // wait if the payment is done or not after opening the UPI app
          handleCancelButtonVisibility();
          // setTimeout(() => {
          //   stopStatusCall();
          //   setShowErroModalForUpiIntent(true);
          // }, WAIT_TIME_FOR_UPI_INTENT_PAYMENT);
        }
      })
      doc.addEventListener('focus', function () {
        setTimeout(() => {
          if (!isMountedRef.current) return;
          if (isIosUserHasBeenNotifiedRef.current && hasUserLeftTheCheckoutScreenRef.current) {
            // APP OPENED
            // close the window popup if the app is opened
            if (isIframe && (isFireFox || isIphone)) {
              doc?.close();
            }
          }
          if (isIosUserHasBeenNotifiedRef.current && !hasUserLeftTheCheckoutScreenRef.current) {
            // USER JUST NOT ALLOWED THE APP TO OPEN OR NO UPI APP ISNTALLED
            isIosUserHasBeenNotifiedRef.current = false;
            stopStatusCall();
            if (isIframe && (isFireFox || isIphone)) {
              doc?.close();
            }
            setTimeout(() => {
              if (!isMountedRef.current) return;
              stopStatusCall();
              setShowErroModalForUpiIntent(true);
            }, 1000);
          }
        }, 2000);
      });
    }
  }
  // UPI INTENT

  useEffect(() => {

    if (isEmpty(processedResponse)) return
    if (processedResponse?.paymentMethod?.type == "Card" && processedResponse?.actions?.some((action) => action?.type === "nativeOtp")) {
      return;
    }
    // UPI INTENT
    if (!isIframe && processedResponse.paymentMethod.brand === 'UpiIntent') {
      popUpWindowForIntentForAndroidRef.current = undefined;
      if (isIphone) {
        hasUserLeftTheCheckoutScreenRef.current = false;
        handleUpiNotOpenedFallbackForIos();
      } else if (isAndroid) {
        hasUserLeftTheCheckoutScreenRef.current = false;
        handleUpiNotOpenedFallbackForAndroid();
      }
    }
    // UPI INTENT

    // handling redirection forms in Iframe
    if (isIframe && processedResponse !== undefined) {
      return handleIframeEvents();
    }

    // normal flow of payment redirection
    let redirectionForm = getFormDataForRedirection();
    if (processedResponse !== undefined && redirectionForm?.submit !== undefined) {
      formDivRef.current.appendChild(redirectionForm);
      redirectionForm.submit();
    }
    if (processedResponse !== undefined && formRef.current && processedResponse.paymentMethod.brand !== 'UpiIntent') {
      formRef.current.submit();
    }


    // this is needed as html react parser doesn't execute javascript
    // if (document.getElementById("autosubmit-form") !== null) {
    //   document.getElementById("autosubmit-form").submit();
    // }
    // if (document.getElementById("payment_post") !== null) {
    //   document.getElementById("payment_post").submit();
    // }
    // if (document.getElementById("threedsChallengeRedirectForm") !== null) {
    //   document.getElementById("threedsChallengeRedirectForm").submit();
    // }
  }, [processedResponse]);

  const resolveAppUrl = (rawUrl) => {
    if (!rawUrl) return "";
    // A plain URL is not valid base64 (contains ":", "/", "."), so use it as-is.
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(rawUrl)) {
      return rawUrl;
    }
    try {
      return atob(rawUrl);
    } catch (e) {
      return rawUrl;
    }
  };
  const addQueryParamsToObject = (url, data) => {
    if (data === undefined) {
      data = {};
    } else {
      data = JSON.parse(JSON.stringify(data));
    }

    if (url.includes("?")) {
      var queryString = url.split("?")[1];

      // Split the query string into an array of key-value pairs
      var params = queryString.split("&");

      // Loop through each key-value pair
      params.forEach(function (param) {
        var pair = param.split("=");
        var key = decodeURIComponent(pair[0]);
        var value = decodeURIComponent(pair[1] || "");

        // Add the key-value pair to the paramsObject
        data[key] = value;
      });
    }
    return data;
  };
  return (
    <>
      {processedResponse?.actions &&
        processedResponse?.actions?.length > 0 &&
        processedResponse?.actions?.filter((action) => action?.type === "html" && action?.htmlPageString !== undefined).map((action, i) => {
          return (
            isIframe ? (<></>) : (
              // isAutoSubmitForm() && isIframe ? (<></>) : (
              <div style={{ visibility: 'hidden' }}>
                <InnerHTML
                  key={`html-${i}`}
                  id="dynamic-html-container"
                  html={action.htmlPageString}
                />
              </div>
            )
          );
        })}
      <div ref={formDivRef} style={{ visibility: 'hidden' }} />

      {response?.actions?.filter((action) => action?.type === "appRedirect").map((action) => {
        const appUrl = resolveAppUrl(action?.url);
        return (
          <form
            style={{ visibility: 'hidden' }}
            key={appUrl}
            method={action?.method}
            ref={formRef}
            action={appUrl}
          >
            {Object.entries(addQueryParamsToObject(appUrl, action?.data)).map((entry) => {
              return (
                <Input
                  key={entry[0]}
                  name={entry[0]}
                  value={entry[1]}
                  type="hidden"
                />
              );
            }
            )}
          </form>
        );
      })}

      {/* UPI INTENT */}
      <ErrorModal
        icon={
          <InfoCircleOutlined style={{ color: "#FF4D4F", fontSize: "22px" }} />
        }
        title={`${t("PAYMENT_COULDNOT_COMPLETE")}`}
        desc={`${t(erroModalDescriptionText.current)}`}
        showModal={showErroModalForUpiIntent}
        primaryOnClick={() => { handlePrimaryClick() }}
        primaryBtnText={`${t("Ok")}`}
        showSecondaryButton={false}
      />
      {/* UPI INTENT */}
    </>
  );
}

export default React.memo(PaymentResponseHandler);
